// Caller journey funnel — session lifecycle as a drop-off funnel.
// Shows how many sessions reach each stage and the drop-off between
// stages. Each stage is a check against fields that live directly on
// the AI session row (no per-session fan-out needed).
//
// Stages:
//   1. Arrived            — sessionStartTime is set
//   2. Bot engaged        — noOfTotalMessages > 0
//   3. Intent classified  — intent is set
//   4. Tool / action fired — totalActionCompleted > 0 OR fulfillmentCount > 0
//   5. Resolution         — interactionOutcome in handled/escalated/abandoned/failed
//
// Reports can be filtered by bot name via the column picker: hidden
// columns remove the per-bot rows.

import React from 'react';
import { Group, Progress, Text } from '@mantine/core';
import { ReportTable } from './ReportTable';

const REPORT_ID = 'flow-funnel';

const STAGES = [
  { id: 'arrived', label: 'Arrived', match: (s) => s.sessionStartTime != null },
  { id: 'engaged', label: 'Bot engaged', match: (s) => (s.noOfTotalMessages ?? 0) > 0 },
  { id: 'classified', label: 'Intent classified', match: (s) => !!s.intent },
  { id: 'tool', label: 'Tool/action fired', match: (s) => (s.totalActionCompleted ?? 0) > 0 || (s.fulfillmentCount ?? 0) > 0 },
  { id: 'resolved', label: 'Resolved', match: (s) => ['HANDLED','COMPLETED','ESCALATED','ABANDONED','FAILED'].includes(s.interactionOutcome) },
];

const COLUMNS = [
  {
    id: 'name',
    label: 'Bot',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.name || <em>(all bots)</em>}</Text>,
  },
  {
    id: 'sessions',
    label: 'Sessions',
    align: 'right',
    render: (r) => <Text size="sm">{r.counts.arrived}</Text>,
  },
  ...STAGES.map((stage, i) => ({
    id: `stage_${stage.id}`,
    label: stage.label,
    hint: i === 0 ? undefined : `Share who reached this stage, and drop-off from previous stage.`,
    render: (r) => <StageCell count={r.counts[stage.id]} total={r.counts.arrived} prev={i > 0 ? r.counts[STAGES[i-1].id] : null} />,
  })),
];

const DEFAULT_VISIBLE = ['sessions', ...STAGES.map((s) => `stage_${s.id}`)];

export function FlowFunnel({ data }) {
  const rows = buildRows(data.sessions);
  return (
    <ReportTable
      title="Caller journey funnel"
      subtitle={`Session lifecycle drop-off per bot · ${rows.length} bot${rows.length === 1 ? '' : 's'} + 1 all-bots roll-up`}
      reportId={REPORT_ID}
      columns={COLUMNS}
      defaultVisibleIds={DEFAULT_VISIBLE}
      rows={rows}
      emptyLabel="No AI Agent sessions recorded in this window."
    />
  );
}

function buildRows(sessions) {
  const perBot = new Map();
  const all = emptyCounts('__all__', 'All bots');
  for (const s of sessions) {
    tally(all, s);
    const name = s.agentName ?? '(unnamed)';
    if (!perBot.has(name)) perBot.set(name, emptyCounts(name, name));
    tally(perBot.get(name), s);
  }
  const rows = [all, ...[...perBot.values()].sort((a, b) => b.counts.arrived - a.counts.arrived)];
  return rows;
}

function emptyCounts(key, name) {
  const counts = { arrived: 0 };
  for (const s of STAGES) counts[s.id] = 0;
  return { key, name: name === '__all__' ? '' : name, counts };
}

function tally(bucket, session) {
  // "arrived" is base: counted for every session whose start time exists.
  // The stage checks are inclusive — bot-engaged implies arrived, etc.
  if (!STAGES[0].match(session)) return;
  for (const s of STAGES) {
    if (s.match(session)) bucket.counts[s.id] += 1;
  }
  bucket.counts.arrived = bucket.counts[STAGES[0].id];
}

function StageCell({ count, total, prev }) {
  if (count == null) return <Text size="xs" c="dimmed">—</Text>;
  const pctOfTotal = total > 0 ? Math.round((count / total) * 100) : 0;
  const dropOff = prev != null && prev > 0 ? Math.round(((prev - count) / prev) * 100) : null;
  return (
    <Group gap={6} align="center" wrap="nowrap">
      <div style={{ minWidth: 60 }}>
        <Text size="sm">{count}</Text>
        <Text size="xs" c="dimmed">{pctOfTotal}%</Text>
      </div>
      <div style={{ flex: 1, minWidth: 60 }}>
        <Progress value={pctOfTotal} size="sm" color={pctOfTotal >= 80 ? 'teal' : pctOfTotal >= 50 ? 'yellow' : 'orange'} />
      </div>
      {dropOff != null && dropOff > 0 && (
        <Text size="xs" c="red" style={{ minWidth: 36, textAlign: 'right' }}>-{dropOff}%</Text>
      )}
    </Group>
  );
}
